import React, { useState, useEffect, useCallback } from 'react';
import { Table, Select, Button, Space, Spin, Empty, Tabs, Tooltip, Divider } from 'antd';
import {
    ReloadOutlined, DollarOutlined, CreditCardOutlined,
} from '@ant-design/icons';
import Layout from '../../components/Layout';
import axiosInstance from '../../variables/api/axiosInstance';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { localeIntl } from '../../i18n/format';
import TableExportButton from '../../components/TableExportButton';
import PageHeader from '../../components/PageHeader';
import logoLocal from '../../assets/images/logo.png';

const ANNEES = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i);

// ── Helpers ────────────────────────────────────────────────────────
const fNb = (v) => (v ?? 0).toLocaleString(localeIntl());
const fMt = (v) => {
    const n = Number(v ?? 0);
    return n.toLocaleString(localeIntl(), { minimumFractionDigits: 0, maximumFractionDigits: 0 });
};
const fPct = (v) => v == null ? '-' : `${v > 0 ? '+' : ''}${v.toFixed(1)} %`;
const varColor = (v) => v == null ? '#6b7280' : v > 0 ? '#166534' : v < 0 ? '#991b1b' : '#374151';

const isTotal = (r) => r._isTotal;
const rowCls = (r) => isTotal(r) ? 'row-total' : '';

// ── Colonnes Encaissements ─────────────────────────────────────────
function colsEnc(annee, t) {
    return [
        {
            title: t('financier.colonnes.bureau'), dataIndex: 'bureau', key: 'b', fixed: 'left', width: 150,
            render: (v, r) => <span style={{ fontWeight: isTotal(r) ? 800 : 600, color: isTotal(r) ? '#1a6b3c' : '#111827' }}>{v}</span>,
        },
        {
            title: String(annee - 1), children: [
                { title: t('financier.colonnes.nb'), dataIndex: 'nbN1', key: 'nb1', align: 'right', width: 70, render: (v, r) => <span style={{ fontWeight: isTotal(r) ? 700 : 400 }}>{fNb(v)}</span> },
                { title: t('financier.colonnes.montantFcfa'), dataIndex: 'montantN1', key: 'm1', align: 'right', width: 140, render: (v, r) => <span style={{ fontWeight: isTotal(r) ? 700 : 400 }}>{fMt(v)}</span> },
                { title: t('financier.colonnes.prodGestion'), dataIndex: 'pgN1', key: 'pg1', align: 'right', width: 120, render: (v, r) => <span style={{ fontWeight: isTotal(r) ? 700 : 400 }}>{fMt(v)}</span> },
            ],
        },
        {
            title: String(annee), children: [
                { title: t('financier.colonnes.nb'), dataIndex: 'nbN', key: 'nb', align: 'right', width: 70, render: (v, r) => <span style={{ fontWeight: isTotal(r) ? 800 : 600, color: isTotal(r) ? '#1a6b3c' : '#111827' }}>{fNb(v)}</span> },
                { title: t('financier.colonnes.montantFcfa'), dataIndex: 'montantN', key: 'm', align: 'right', width: 140, render: (v, r) => <span style={{ fontWeight: isTotal(r) ? 800 : 600, color: isTotal(r) ? '#1a6b3c' : '#111827' }}>{fMt(v)}</span> },
                { title: t('financier.colonnes.prodGestion'), dataIndex: 'pgN', key: 'pg', align: 'right', width: 120, render: (v, r) => <span style={{ fontWeight: isTotal(r) ? 800 : 600, color: isTotal(r) ? '#1a6b3c' : '#111827' }}>{fMt(v)}</span> },
            ],
        },
        {
            title: t('financier.colonnes.variations'), children: [
                { title: t('financier.colonnes.nb'), dataIndex: 'varNb', key: 'vn', align: 'right', width: 90, render: (v) => <span style={{ fontWeight: 600, color: varColor(v) }}>{fPct(v)}</span> },
                { title: t('financier.colonnes.montant'), dataIndex: 'varMontant', key: 'vm', align: 'right', width: 90, render: (v) => <span style={{ fontWeight: 600, color: varColor(v) }}>{fPct(v)}</span> },
                { title: t('financier.colonnes.prodGestion'), dataIndex: 'varPg', key: 'vpg', align: 'right', width: 100, render: (v) => <span style={{ fontWeight: 600, color: varColor(v) }}>{fPct(v)}</span> },
            ],
        },
    ];
}

// ── Colonnes Paiements ─────────────────────────────────────────────
function colsPay(annee, t) {
    return [
        {
            title: t('financier.colonnes.beneficiaire'), dataIndex: 'beneficiaire', key: 'b', fixed: 'left', width: 150,
            render: (v, r) => <span style={{ fontWeight: isTotal(r) ? 800 : 600, color: isTotal(r) ? '#1a6b3c' : '#111827' }}>{v}</span>,
        },
        {
            title: String(annee - 1), children: [
                { title: t('financier.colonnes.nb'), dataIndex: 'nbN1', key: 'nb1', align: 'right', width: 70, render: (v, r) => <span style={{ fontWeight: isTotal(r) ? 700 : 400 }}>{fNb(v)}</span> },
                { title: t('financier.colonnes.montantFcfa'), dataIndex: 'montantN1', key: 'm1', align: 'right', width: 140, render: (v, r) => <span style={{ fontWeight: isTotal(r) ? 700 : 400 }}>{fMt(v)}</span> },
            ],
        },
        {
            title: String(annee), children: [
                { title: t('financier.colonnes.nb'), dataIndex: 'nbN', key: 'nb', align: 'right', width: 70, render: (v, r) => <span style={{ fontWeight: isTotal(r) ? 800 : 600, color: isTotal(r) ? '#1a6b3c' : '#111827' }}>{fNb(v)}</span> },
                { title: t('financier.colonnes.montantFcfa'), dataIndex: 'montantN', key: 'm', align: 'right', width: 140, render: (v, r) => <span style={{ fontWeight: isTotal(r) ? 800 : 600, color: isTotal(r) ? '#1a6b3c' : '#111827' }}>{fMt(v)}</span> },
            ],
        },
        {
            title: t('financier.colonnes.variations'), children: [
                { title: t('financier.colonnes.nb'), dataIndex: 'varNb', key: 'vn', align: 'right', width: 90, render: (v) => <span style={{ fontWeight: 600, color: varColor(v) }}>{fPct(v)}</span> },
                { title: t('financier.colonnes.montant'), dataIndex: 'varMontant', key: 'vm', align: 'right', width: 90, render: (v) => <span style={{ fontWeight: 600, color: varColor(v) }}>{fPct(v)}</span> },
            ],
        },
        {
            title: t('financier.colonnes.partAnnee', { annee }), dataIndex: 'partMontant', key: 'part', align: 'right', width: 100,
            render: (v) => v == null ? '-' : <span style={{ fontWeight: 600 }}>{v.toFixed(1)} %</span>,
        },
    ];
}

// ── Colonnes Dont Togo ─────────────────────────────────────────────
function colsTogo(annee, t) {
    return [
        {
            title: t('financier.colonnes.compagnie'), dataIndex: 'compagnie', key: 'c', width: 200,
            render: (v, r) => <span style={{ fontWeight: isTotal(r) ? 800 : 500 }}>{v}</span>,
        },
        { title: t('financier.colonnes.nbAnnee', { annee: annee - 1 }), dataIndex: 'nbN1', key: 'nb1', align: 'right', width: 80, render: (v) => fNb(v) },
        { title: t('financier.colonnes.montantAnnee', { annee: annee - 1 }), dataIndex: 'montantN1', key: 'm1', align: 'right', width: 140, render: (v, r) => <span style={{ fontWeight: isTotal(r) ? 700 : 400 }}>{fMt(v)}</span> },
        { title: t('financier.colonnes.nbAnnee', { annee }), dataIndex: 'nbN', key: 'nb', align: 'right', width: 80, render: (v, r) => <span style={{ fontWeight: isTotal(r) ? 700 : 600, color: isTotal(r) ? '#1a6b3c' : '#111827' }}>{fNb(v)}</span> },
        { title: t('financier.colonnes.montantAnnee', { annee }), dataIndex: 'montantN', key: 'm', align: 'right', width: 140, render: (v, r) => <span style={{ fontWeight: isTotal(r) ? 800 : 600, color: isTotal(r) ? '#1a6b3c' : '#111827' }}>{fMt(v)}</span> },
    ];
}

// ── Colonnes d'export (plates, pour TableExportButton) ─────────────
function colsEncExport(annee, t) {
    return [
        { title: t('financier.colonnes.bureau'), dataIndex: 'bureau' },
        { title: t('financier.colonnes.nbAnnee', { annee: annee - 1 }), dataIndex: 'nbN1', numeric: true },
        { title: t('financier.colonnes.montantAnnee', { annee: annee - 1 }), dataIndex: 'montantN1', format: 'currency' },
        { title: t('financier.colonnes.prodGestionAnnee', { annee: annee - 1 }), dataIndex: 'pgN1', format: 'currency' },
        { title: t('financier.colonnes.nbAnnee', { annee }), dataIndex: 'nbN', numeric: true },
        { title: t('financier.colonnes.montantAnnee', { annee }), dataIndex: 'montantN', format: 'currency' },
        { title: t('financier.colonnes.prodGestionAnnee', { annee }), dataIndex: 'pgN', format: 'currency' },
        { title: t('financier.colonnes.varNb'), dataIndex: 'varNb', export: (v) => v == null ? '' : `${v > 0 ? '+' : ''}${v.toFixed(1)} %` },
        { title: t('financier.colonnes.varMontant'), dataIndex: 'varMontant', export: (v) => v == null ? '' : `${v > 0 ? '+' : ''}${v.toFixed(1)} %` },
        { title: t('financier.colonnes.varProdGestion'), dataIndex: 'varPg', export: (v) => v == null ? '' : `${v > 0 ? '+' : ''}${v.toFixed(1)} %` },
    ];
}

function colsPayExport(annee, t) {
    return [
        { title: t('financier.colonnes.beneficiaire'), dataIndex: 'beneficiaire' },
        { title: t('financier.colonnes.nbAnnee', { annee: annee - 1 }), dataIndex: 'nbN1', numeric: true },
        { title: t('financier.colonnes.montantAnnee', { annee: annee - 1 }), dataIndex: 'montantN1', format: 'currency' },
        { title: t('financier.colonnes.nbAnnee', { annee }), dataIndex: 'nbN', numeric: true },
        { title: t('financier.colonnes.montantAnnee', { annee }), dataIndex: 'montantN', format: 'currency' },
        { title: t('financier.colonnes.varNb'), dataIndex: 'varNb', export: (v) => v == null ? '' : `${v > 0 ? '+' : ''}${v.toFixed(1)} %` },
        { title: t('financier.colonnes.varMontant'), dataIndex: 'varMontant', export: (v) => v == null ? '' : `${v > 0 ? '+' : ''}${v.toFixed(1)} %` },
        { title: t('financier.colonnes.partAnnee', { annee }), dataIndex: 'partMontant', export: (v) => v == null ? '' : `${v.toFixed(1)} %` },
    ];
}

function colsTogoExport(annee, t) {
    return [
        { title: t('financier.colonnes.compagnie'), dataIndex: 'compagnie' },
        { title: t('financier.colonnes.nbAnnee', { annee: annee - 1 }), dataIndex: 'nbN1', numeric: true },
        { title: t('financier.colonnes.montantAnnee', { annee: annee - 1 }), dataIndex: 'montantN1', format: 'currency' },
        { title: t('financier.colonnes.nbAnnee', { annee }), dataIndex: 'nbN', numeric: true },
        { title: t('financier.colonnes.montantAnnee', { annee }), dataIndex: 'montantN', format: 'currency' },
    ];
}

// ── Calcul total Dont Togo ─────────────────────────────────────────
function totalTogo(lignes) {
    return {
        compagnie: 'TOTAL',
        nbN1: lignes.reduce((s, r) => s + (r.nbN1 || 0), 0),
        montantN1: lignes.reduce((s, r) => s + Number(r.montantN1 || 0), 0),
        nbN: lignes.reduce((s, r) => s + (r.nbN || 0), 0),
        montantN: lignes.reduce((s, r) => s + Number(r.montantN || 0), 0),
        _isTotal: true,
        key: 'total',
    };
}

// ════════════════════════════════════════════════════════════════════
//  COMPOSANT
// ════════════════════════════════════════════════════════════════════
export default function EtatFinancier() {
    const { t } = useTranslation('statistiques');
    const [onglet, setOnglet] = useState('enc');
    const [annee, setAnnee] = useState(new Date().getFullYear());
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await axiosInstance.get(`/v1/stats/financier?annee=${annee}`);
            setData(res.data?.data);
        } catch { toast.error(t('financier.erreurChargement')); }
        finally { setLoading(false); }
    }, [annee, t]);

    useEffect(() => { load(); }, [load]);

    // Préparer les données avec clés et lignes total
    const encData = data ? [
        ...(data.encaissements || []).map((r, i) => ({ ...r, key: i })),
        { ...(data.totalEncaissements || {}), key: 'total', _isTotal: true },
    ] : [];

    const payData = data ? [
        ...(data.paiements || []).map((r, i) => ({ ...r, key: i })),
        { ...(data.totalPaiements || {}), key: 'total', _isTotal: true },
    ] : [];

    const encTogoData = data ? [
        ...(data.encaissementsTogo || []).map((r, i) => ({ ...r, key: i })),
        ...(data.encaissementsTogo?.length ? [totalTogo(data.encaissementsTogo)] : []),
    ] : [];

    const payTogoData = data ? [
        ...(data.paiementsTogo || []).map((r, i) => ({ ...r, key: i })),
        ...(data.paiementsTogo?.length ? [totalTogo(data.paiementsTogo)] : []),
    ] : [];

    const TableVide = () => <Empty description={t('commun.aucuneDonnee')} style={{ padding: 32 }} />;

    return (
        <Layout title={t('financier.titreOnglet')}>
            <div style={{ maxWidth: 1400 }}>

                {/* Pas de `compte` : l'ecran empile deux onglets (encaissements,
                    paiements) et leurs sous-tableaux « dont Togo », sans onglet
                    actif connu ici - aucun nombre unique ne serait juste. */}
                <PageHeader
                    titre={t('financier.titre')}
                    icone={<DollarOutlined />}
                    actions={(
                        <Space>
                            <Select
                                value={annee}
                                onChange={setAnnee}
                                style={{ width: 100, borderRadius: 8 }}
                                options={ANNEES.map(a => ({ value: a, label: a }))}
                            />
                            <Tooltip title={t('commun.actualiser')}>
                                <Button icon={<ReloadOutlined />} onClick={load} loading={loading} style={{ borderRadius: 8 }} />
                            </Tooltip>
                        </Space>
                    )}
                />

                <Spin spinning={loading}>
                    {/* ONGLETS CONTROLES : chaque volet porte l'export de son tableau
                        principal, et il ne doit y en avoir qu'un au coin bas-droite.
                        AntD garde montes les volets deja visites. */}
                    <Tabs
                        activeKey={onglet}
                        onChange={setOnglet}
                        items={[
                            // ── Onglet Encaissements ──
                            {
                                key: 'enc',
                                label: <span><DollarOutlined /> {t('financier.encaissements')}</span>,
                                children: (
                                    <div className="cb-card" style={{ padding: 0 }}>
                                        <div style={{ padding: '14px 20px 8px', borderBottom: '1px solid #f3f4f6', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
                                            <span style={{ fontWeight: 700, color: '#1a6b3c', fontSize: 13 }}>
                                                {t('financier.titreEncaissements', { annee })}
                                            </span>
                                            {onglet === 'enc' && (
                                              <TableExportButton
                                                  floating
                                                  data={encData}
                                                  columns={colsEncExport(annee, t)}
                                                  filename={`etat-financier-encaissements-${annee}`}
                                                  title={t('financier.export.titreEncaissements', { annee })}
                                                  subtitle={t('financier.export.sousTitreEncaissements', { annee, anneePrecedente: annee - 1, date: data?.dateArrete || '-' })}
                                                  logo={logoLocal}
                                                  size="small"
                                                  disabled={!data || encData.length <= 1}
                                              />
                                            )}
                                        </div>
                                        {!loading && encData.length <= 1 ? <TableVide /> : (
                                            <Table
                                                className="cb-table"
                                                dataSource={encData}
                                                columns={colsEnc(annee, t)}
                                                pagination={false}
                                                size="small"
                                                bordered
                                                scroll={{ x: 1100 }}
                                                rowClassName={rowCls}
                                            />
                                        )}

                                        {/* Dont Togo · Encaissements */}
                                        {encTogoData.length > 1 && (
                                            <>
                                                <Divider style={{ margin: '0 20px' }}>
                                                    <span style={{ fontSize: 12, color: '#1a6b3c', fontWeight: 700 }}>
                                                        {t('financier.dontTogoEncaissements')}
                                                    </span>
                                                </Divider>
                                                <div style={{ padding: '0 20px 8px', textAlign: 'right' }}>
                                                    <TableExportButton
                                                        data={encTogoData}
                                                        columns={colsTogoExport(annee, t)}
                                                        filename={`etat-financier-encaissements-togo-${annee}`}
                                                        title={t('financier.export.titreTogoEncaissements', { annee })}
                                                        subtitle={t('financier.export.sousTitreTogo', { annee, anneePrecedente: annee - 1, date: data?.dateArrete || '-' })}
                                                        logo={logoLocal}
                                                        size="small"
                                                    />
                                                </div>
                                                <Table
                                                    className="cb-table"
                                                    dataSource={encTogoData}
                                                    columns={colsTogo(annee, t)}
                                                    pagination={false}
                                                    size="small"
                                                    scroll={{ x: 700 }}
                                                    rowClassName={rowCls}
                                                    style={{ marginBottom: 16 }}
                                                />
                                            </>
                                        )}
                                    </div>
                                ),
                            },

                            // ── Onglet Paiements ──
                            {
                                key: 'pay',
                                label: <span><CreditCardOutlined /> {t('financier.paiements')}</span>,
                                children: (
                                    <div className="cb-card" style={{ padding: 0 }}>
                                        <div style={{ padding: '14px 20px 8px', borderBottom: '1px solid #f3f4f6', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
                                            <span style={{ fontWeight: 700, color: '#1a6b3c', fontSize: 13 }}>
                                                {t('financier.titrePaiements', { annee })}
                                            </span>
                                            {onglet === 'pay' && (
                                              <TableExportButton
                                                  floating
                                                  data={payData}
                                                  columns={colsPayExport(annee, t)}
                                                  filename={`etat-financier-paiements-${annee}`}
                                                  title={t('financier.export.titrePaiements', { annee })}
                                                  subtitle={t('financier.export.sousTitrePaiements', { annee, anneePrecedente: annee - 1, date: data?.dateArrete || '-' })}
                                                  logo={logoLocal}
                                                  size="small"
                                                  disabled={!data || payData.length <= 1}
                                              />
                                            )}
                                        </div>
                                        {!loading && payData.length <= 1 ? <TableVide /> : (
                                            <Table
                                                className="cb-table"
                                                dataSource={payData}
                                                columns={colsPay(annee, t)}
                                                pagination={false}
                                                size="small"
                                                bordered
                                                scroll={{ x: 900 }}
                                                rowClassName={rowCls}
                                            />
                                        )}

                                        {/* Dont Togo · Paiements */}
                                        {payTogoData.length > 1 && (
                                            <>
                                                <Divider style={{ margin: '0 20px' }}>
                                                    <span style={{ fontSize: 12, color: '#1a6b3c', fontWeight: 700 }}>
                                                        {t('financier.dontTogoPaiements')}
                                                    </span>
                                                </Divider>
                                                <div style={{ padding: '0 20px 8px', textAlign: 'right' }}>
                                                    <TableExportButton
                                                        data={payTogoData}
                                                        columns={colsTogoExport(annee, t)}
                                                        filename={`etat-financier-paiements-togo-${annee}`}
                                                        title={t('financier.export.titreTogoPaiements', { annee })}
                                                        subtitle={t('financier.export.sousTitreTogo', { annee, anneePrecedente: annee - 1, date: data?.dateArrete || '-' })}
                                                        logo={logoLocal}
                                                        size="small"
                                                    />
                                                </div>
                                                <Table
                                                    className="cb-table"
                                                    dataSource={payTogoData}
                                                    columns={colsTogo(annee, t)}
                                                    pagination={false}
                                                    size="small"
                                                    scroll={{ x: 700 }}
                                                    rowClassName={rowCls}
                                                    style={{ marginBottom: 16 }}
                                                />
                                            </>
                                        )}
                                    </div>
                                ),
                            },
                        ]}
                    />
                </Spin>

                {data && (
                    <div style={{ marginTop: 8, fontSize: 11, color: '#9ca3af', textAlign: 'right' }}>
                        {t('commun.sourcePointGenereLe', { date: new Date().toLocaleDateString(localeIntl()) })}
                    </div>
                )}
            </div>

            <style>{`
        .row-total td { background: #f0faf4 !important; font-weight: 700 !important; }
        .row-total:hover td { background: #dcfce7 !important; }
      `}</style>
        </Layout>
    );
}